import dynamic from "next/dynamic";
import { useRouter } from "next/router";
import { useEffect, useLayoutEffect, useRef, useState } from "react";
import { useScrollDirection } from "react-use-scroll-direction";

import { useShallow } from "zustand/react/shallow";

import { cn } from "../../lib/utils";
import useCart from "../../store/cart-store";
import useStore from "../../store/search-store";
import CartIconSvg from "./CartIconSvg";
import CloseIconSvg from "./CloseIconSvg";
import MenuIcon from "./MenuIcon";
import SearchIconSvg from "./SearchIconSvg";

const SearchSidebar = dynamic(() => import("./SearchSidebar"), {
  loading: () => null,
  ssr: false,
});

const useIsomorphicLayoutEffect = typeof window !== "undefined" ? useLayoutEffect : useEffect;

const NavBar = (props) => {
  const [direction, setDirection] = useState("");
  const hasHydrated = useCart((state) => state.hasHydrated);
  const cartHasItems = useCart((state) => state.products.length > 0);
  const { isScrollingUp, isScrollingDown } = useScrollDirection();
  const { isSearchOpen, setIsSearchOpen, shouldSearchReopen, setShouldSearchReopen } = useStore(
    useShallow((state) => ({
      isSearchOpen: state.isSearchOpen,
      setIsSearchOpen: state.setIsSearchOpen,
      shouldSearchReopen: state.shouldSearchReopen,
      setShouldSearchReopen: state.setShouldSearchReopen,
    })),
  );
  const searchInputRef = useRef(null);
  const headerRef = useRef(null);
  const router = useRouter();
  const { homePage = false, showSearch = true, sideNavOpen = false } = props;
  const storeName = props.store?.name || props.storeName || "";

  // Re-open search when returning to the path that set shouldSearchReopen;
  // close on other route changes. Do not depend on isSearchOpen — that made
  // every manual icon-open immediately close again.
  useEffect(() => {
    if (shouldSearchReopen === router.asPath) {
      setIsSearchOpen(true);
    } else {
      setIsSearchOpen(false);
    }
  }, [router.asPath, shouldSearchReopen, setIsSearchOpen]);

  useEffect(() => {
    isScrollingDown && setDirection("down");
    isScrollingUp && setDirection("up");
  }, [isScrollingDown, isScrollingUp]);

  useIsomorphicLayoutEffect(() => {
    if (!isSearchOpen && !sideNavOpen) return;
    const header = headerRef.current;
    if (!header) return;
    const bottom = header.getBoundingClientRect().bottom;
    document.documentElement.style.setProperty("--store-chrome-bottom", `${bottom}px`);
  }, [isSearchOpen, sideNavOpen]);

  useEffect(() => {
    document.body.style.overflow = isSearchOpen || sideNavOpen ? "hidden" : "";
    return () => {
      document.body.style.overflow = "";
    };
  }, [isSearchOpen, sideNavOpen]);

  const checkoutShouldHide = () => {
    if (typeof window !== "undefined" && window.scrollY > 120) {
      return true;
    }
    return false;
  };

  return (
    <>
      <header
        ref={headerRef}
        className={
          homePage
            ? cn(
                "sticky top-0 z-50 flex h-[60px] w-full items-center bg-background font-montserrat transition-[top,opacity] duration-300",
                !isSearchOpen && !sideNavOpen && direction === "down" && checkoutShouldHide()
                  ? "-top-16 opacity-0"
                  : "top-0 opacity-100",
              )
            : cn(
                "fixed left-1/2 top-0 z-[99] mb-5 flex h-[60px] w-full max-w-[var(--max-width)] -translate-x-1/2 items-center bg-background font-montserrat transition-[top,opacity] duration-300 ease-in-out",
                !isSearchOpen && !sideNavOpen && direction === "down" && checkoutShouldHide()
                  ? "-top-[60px]"
                  : "",
              )
        }
      >
        <div className="mx-auto flex h-full w-full max-w-[var(--max-width)] items-center gap-4 px-5">
          <MenuIcon
            open={sideNavOpen}
            onClick={() => {
              if (isSearchOpen) {
                setShouldSearchReopen(null);
                setIsSearchOpen(false);
              }
              props.toggleSideNav?.();
            }}
          />

          <h2
            className="min-w-0 flex-1 cursor-pointer truncate text-[15px] font-semibold text-checkoutText"
            onClick={() => router.push("/")}
          >
            {storeName}
          </h2>

          <div className="flex shrink-0 items-center gap-4">
            {showSearch && (
              <button
                type="button"
                className="flex h-5 w-5 cursor-pointer items-center justify-center text-checkoutText"
                aria-label={isSearchOpen ? "Close search menu" : "Open search menu"}
                onClick={() => {
                  if (isSearchOpen) {
                    setShouldSearchReopen(null);
                    setIsSearchOpen(false);
                    return;
                  }
                  if (sideNavOpen) props.toggleSideNav?.();
                  setIsSearchOpen(true);
                  if (searchInputRef.current) searchInputRef.current.focus();
                }}
              >
                {isSearchOpen ? <CloseIconSvg color="currentColor" /> : <SearchIconSvg />}
              </button>
            )}

            {!props.hideInAdvance && (
              <button
                type="button"
                className="-mr-2.5 mb-0.5 flex h-[38px] w-[38px] cursor-pointer items-center justify-center"
                aria-label="Open cart"
                onClick={() => {
                  if (sideNavOpen) props.toggleSideNav?.();
                  if (isSearchOpen) {
                    setShouldSearchReopen(null);
                    setIsSearchOpen(false);
                  }
                  props.handleShowCart?.(true);
                }}
              >
                <CartIconSvg filled={hasHydrated && cartHasItems} />
              </button>
            )}
          </div>
        </div>
      </header>

      <SearchSidebar
        isOpen={isSearchOpen}
        toggleDrawer={() => setIsSearchOpen(!isSearchOpen)}
        store={props.store}
        searchRef={searchInputRef}
      />
    </>
  );
};

export default NavBar;
